/**
 * Creates an API client.
 *
 * @param apiKey
 * @returns
 */
export const createClient = (apiKey: string) => {
  return {
    /**
     * Returns declinations for the provided params
     * @param {DeclinationParams<Name, LastName>} params Parameters for declination
     * @param {FetchInit} [options] Option passed to internal components (for example
     * `options.fetchInit` will be passed to fetch)
     * @example { name: 'Petr' } -> { name: 'Petře' }
     * @example { lastName: 'Svoboda' } -> { name: 'Svobodo' }
     * @example { name: 'Petr', lastName: 'Svoboda' } -> { name: 'Petře', lastName: 'Svobodo' }
     * @example { lastName: 'Svobodová', sex: 'f' } -> { lastName: 'Svobodová' }
     * @returns
     */
    getDeclination: <
      Name extends string | undefined = undefined,
      LastName extends string | undefined = undefined
    >(
      params: DeclinationParams<Name, LastName>,
      options?: { fetchInit?: RequestInit }
    ) => _getDeclination(params, apiKey, options),
  };
};

const API_URL = "https://api.5pad.cz";
const DECLINATION_URL = API_URL + "/declination";

type DeclinationParams<
  Name extends string | undefined,
  LastName extends string | undefined
> = {
  case?: "vokativ";
} & (
  | {
      name: Name;
    }
  | { lastName: LastName }
) & { sex?: "m" | "f" };

type DeclinationResult<
  Name extends string | undefined,
  LastName extends string | undefined
> = {
  name: Name extends string ? string : never;
  lastName: LastName extends string ? string : never;
};

const _getDeclination = async <
  Name extends string | undefined = undefined,
  LastName extends string | undefined = undefined
>(
  params: DeclinationParams<Name, LastName>,
  apiKey: string,
  options?: { fetchInit?: RequestInit }
): Promise<
  DeclinationResult<
    Name extends string ? string : undefined,
    LastName extends string ? string : undefined
  >
> => {
  if (
    !(
      ("name" in params && params.name) ||
      ("lastName" in params && params.lastName)
    )
  ) {
    throw new Error("'name' or 'lastName' parameters must be specified");
  }

  let url = `${DECLINATION_URL}/${params.case ?? "vokativ"}?`;

  let hasName = false,
    hasLastName = false;
  if ("name" in params && params.name) {
    hasName = true;
    url += `name=${encodeURIComponent(params.name)}&`;
  }
  if ("lastName" in params && params.lastName) {
    hasLastName = true;
    url += `last_name=${encodeURIComponent(params.lastName)}&`;
  }
  if (params.sex) {
    url += `sex=${encodeURIComponent(params.sex)}&`;
  }
  url = url.slice(0, -1);

  const result = await fetch(url, {
    ...options?.fetchInit,
    headers: {
      ...options?.fetchInit?.headers,
      "content-type": "application/json",
      authorization: "Bearer " + apiKey,
    },
  });

  const body = await result.json();

  if (!result.ok) {
    throw new Error(body);
  }

  const declination: Record<string, string> = {};
  if (hasName) {
    declination.name = body.name;
  }
  if (hasLastName) {
    declination.lastName = body.last_name;
  }

  return declination as DeclinationResult<
    Name extends string ? string : undefined,
    LastName extends string ? string : undefined
  >;
};
